import { noConflictsLabel } from 'owa-locstrings/lib/strings/noconflictslabel.locstring.json';
import {
    loadingConflictsLabel,
    failedToLoadConflictsLabel,
    hideConflictsButton,
    singleConflictLabel,
    multipleConflictsLabel,
    showConflictsButton,
    hideAdjacentItemsButton,
    showAdjacentItemsButton,
    precedingMeetingsHeaderLabel,
    conflictingMeetingsHeaderLabel,
    followingMeetingsHeaderLabel,
} from './MeetingRequestConflicts.locstring.json';
import loc, { format } from 'owa-localize';
import ConflictingMeetingList from './ConflictingMeetingList';
import initializeAdjacentAndConflictingMeetingsAction from '../actions/initializeAdjacentAndConflictingMeetingsAction';
import toggleShowHideConflicts from '../actions/toggleShowHideConflicts';
import { MeetingRequestViewState } from '../store/schema/MeetingRequestViewState';
import RequestStatus from '../store/schema/RequestStatus';
import { default as logMeetingMessageAction } from '../utils/logMeetingMessageAction';
import MeetingMessageActionEntrySource from '../store/schema/MeetingMessageActionEntrySource';
import { observer } from 'mobx-react';
import { Link, ILinkProps, ILinkStyles } from 'office-ui-fabric-react/lib/Link';
import { IStyleFunction } from 'office-ui-fabric-react/lib/Utilities';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import { observableNow } from 'owa-observable-datetime';
import CalendarItem from 'owa-service/lib/contract/CalendarItem';
import MeetingRequestMessageType from 'owa-service/lib/contract/MeetingRequestMessageType';
import { lazyCloseConflictsView, lazyOpenConflictsView } from 'owa-time-panel';
import { isTimePanelAvailable } from 'owa-time-panel-bootstrap';
import { isShowingConflictsView } from 'owa-time-panel-view-stack';
import * as React from 'react';
import {
    getFutureConflictingMeetings,
    getFutureAdjacentMeetings,
} from '../utils/getMeetingRequestConflicts';

const styles = require('./MeetingRequestConflicts.scss');
const classNames = require('classnames/bind').bind(styles);

export interface MeetingRequestConflictsProps {
    item: MeetingRequestMessageType;
    viewState: MeetingRequestViewState;
    entrySource: MeetingMessageActionEntrySource;
    className?: string;
    linkClassName?: string;
    linkStyles?: IStyleFunction<ILinkProps, ILinkStyles>;
}

@observer
export default class MeetingRequestConflicts extends React.Component<
    MeetingRequestConflictsProps,
    {}
> {
    componentDidMount() {
        this.initializeAdjacentAndConflictingMeetingsIfNeeded();
    }

    componentDidUpdate() {
        this.initializeAdjacentAndConflictingMeetingsIfNeeded();
    }

    render() {
        const { viewState } = this.props;

        if (!viewState) {
            // This happens everytime right after user clicks on this calendar item, because the meetingRequestViewState hasn't been initialized yet
            return null;
        }

        return this.createConflictsInfoRow();
    }

    private createConflictsInfoRow = (): JSX.Element => {
        const { item, viewState, className } = this.props;

        let conflictInfoRow;

        if (item.AdjacentMeetings || item.ConflictingMeetings) {
            // Show conflict if client already has the adjacent meetings or conflicting meetings
            conflictInfoRow = this.createConflictContent();
        } else {
            // Otherwise, show controls(loading, error) depending on the service call status
            switch (viewState.conflictsRequestStatus) {
                case RequestStatus.Pending:
                    conflictInfoRow = (
                        <div className={styles.loadingContainer}>
                            <Spinner className={styles.loadingSpinner} size={SpinnerSize.small} />
                            <span className={styles.loadingLabel}>
                                {' '}
                                {loc(loadingConflictsLabel)}{' '}
                            </span>
                        </div>
                    );
                    break;

                case RequestStatus.Error:
                    conflictInfoRow = (
                        <div>
                            <span className={styles.noConflictLabel}>
                                {loc(failedToLoadConflictsLabel)}
                            </span>
                        </div>
                    );
                    break;

                case RequestStatus.Complete:
                    conflictInfoRow = this.createConflictContent();
                    break;

                case RequestStatus.NotInitiated:
                default:
                    conflictInfoRow = null;
                    break;
            }
        }

        return (
            conflictInfoRow && (
                <div className={classNames(styles.container, className)}> {conflictInfoRow} </div>
            )
        );
    };

    private createConflictContent = (): JSX.Element => {
        const { item, viewState, linkClassName, linkStyles } = this.props;
        const canShowTimePanel = isTimePanelAvailable();
        const now = observableNow();
        const futureAdjacentMeetings = getFutureAdjacentMeetings(item, now);
        const futurePrecedingMeetings = futureAdjacentMeetings.filter(this.isMeetingPreceding);
        const futureFollowingMeetings = futureAdjacentMeetings.filter(this.isMeetingFollowing);
        const futureConflictingMeetings = getFutureConflictingMeetings(item, now);
        const hasConflicts =
            futurePrecedingMeetings.length +
                futureConflictingMeetings.length +
                futureFollowingMeetings.length >
            0;

        let openCloseLabel: string;
        if (canShowTimePanel) {
            if (isShowingConflictsView()) {
                openCloseLabel = loc(hideConflictsButton);
            } else {
                switch (futureConflictingMeetings.length) {
                    case 0:
                        openCloseLabel = loc(noConflictsLabel);
                        break;
                    case 1:
                        openCloseLabel = loc(singleConflictLabel);
                        break;
                    default:
                        openCloseLabel = format(
                            loc(multipleConflictsLabel),
                            futureConflictingMeetings.length
                        );
                        break;
                }
            }
        } else {
            if (!hasConflicts) {
                openCloseLabel = loc(noConflictsLabel);
            } else if (futureConflictingMeetings.length > 0) {
                openCloseLabel = viewState.conflictsShown
                    ? loc(hideConflictsButton)
                    : loc(showConflictsButton);
            } else {
                openCloseLabel = viewState.conflictsShown
                    ? loc(hideAdjacentItemsButton)
                    : loc(showAdjacentItemsButton);
            }
        }

        const isRecurringMaster = this.isMeetingRecurringMaster();

        return (
            <div>
                <Link
                    className={classNames(
                        hasConflicts || canShowTimePanel
                            ? styles.showHideButton
                            : styles.noConflictLabel,
                        linkClassName
                    )}
                    styles={linkStyles}
                    onClick={this.getClickHandler(hasConflicts)}>
                    {openCloseLabel}
                </Link>
                {!canShowTimePanel && hasConflicts && viewState.conflictsShown && (
                    <div className={styles.conflictList}>
                        {futurePrecedingMeetings.length > 0 && (
                            <ConflictingMeetingList
                                items={futurePrecedingMeetings}
                                headerText={loc(precedingMeetingsHeaderLabel)}
                                isMeetingRequestRecurringMaster={isRecurringMaster}
                            />
                        )}
                        {futureConflictingMeetings.length > 0 && (
                            <ConflictingMeetingList
                                items={futureConflictingMeetings}
                                headerText={loc(conflictingMeetingsHeaderLabel)}
                                isMeetingRequestRecurringMaster={isRecurringMaster}
                            />
                        )}
                        {futureFollowingMeetings.length > 0 && (
                            <ConflictingMeetingList
                                items={futureFollowingMeetings}
                                headerText={loc(followingMeetingsHeaderLabel)}
                                isMeetingRequestRecurringMaster={isRecurringMaster}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    };

    private isMeetingRecurringMaster = (): boolean => {
        return this.props.item.CalendarItemType === 'RecurringMaster';
    };

    private isMeetingPreceding = (adjacentMeeting: CalendarItem): boolean => {
        if (this.isMeetingRecurringMaster()) {
            // For recurring meetings, client only compares the time part with adjacent meetings, as the adjacent meetings might be instances on a different date.
            const adjacentMeetingEndTimePortion =
                new Date(adjacentMeeting.End).getTime() % TimeConstants.MillisecondsInOneDay;
            const meetingRequestStartTimePortion =
                new Date(this.props.item.Start).getTime() % TimeConstants.MillisecondsInOneDay;
            return adjacentMeetingEndTimePortion <= meetingRequestStartTimePortion;
        } else {
            return (
                new Date(adjacentMeeting.End).getTime() <= new Date(this.props.item.Start).getTime()
            );
        }
    };

    private isMeetingFollowing = (adjacentMeeting: CalendarItem): boolean => {
        if (this.isMeetingRecurringMaster()) {
            // For recurring meetings, client only compares the time part with adjacent meetings, as the adjacent meetings might be instances on a different date.
            const adjacentMeetingStartTimePortion =
                new Date(adjacentMeeting.Start).getTime() % TimeConstants.MillisecondsInOneDay;
            const meetingRequestEndTimePortion =
                new Date(this.props.item.End).getTime() % TimeConstants.MillisecondsInOneDay;
            return adjacentMeetingStartTimePortion >= meetingRequestEndTimePortion;
        } else {
            return (
                new Date(adjacentMeeting.Start).getTime() >= new Date(this.props.item.End).getTime()
            );
        }
    };

    private onShowHideConflicts = (evt: React.MouseEvent<unknown>) => {
        // Stop the click event for the outter container.
        evt.stopPropagation();
        toggleShowHideConflicts(this.props.viewState);
        let additionalProps = [];

        if (this.props.item.AdjacentMeetings && this.props.item.AdjacentMeetings.Items.length) {
            additionalProps.push('HasAdjacents');
        }
        if (
            this.props.item.ConflictingMeetings &&
            this.props.item.ConflictingMeetings.Items.length
        ) {
            additionalProps.push('HasConflicts');
        }
        logMeetingMessageAction(
            this.props.viewState.conflictsShown ? 'HideConflicts' : 'ShowConflicts',
            this.props.item,
            this.props.entrySource,
            additionalProps
        );
    };

    private onShowHideConflictsInSidePanel = (evt: React.MouseEvent<unknown>) => {
        let additionalProps = [];

        if (this.props.item.AdjacentMeetings && this.props.item.AdjacentMeetings.Items.length) {
            additionalProps.push('HasAdjacents');
        }
        if (
            this.props.item.ConflictingMeetings &&
            this.props.item.ConflictingMeetings.Items.length
        ) {
            additionalProps.push('HasConflicts');
        }

        if (isShowingConflictsView()) {
            lazyCloseConflictsView.importAndExecute();
            logMeetingMessageAction(
                'HideConflicts',
                this.props.item,
                this.props.entrySource,
                additionalProps
            );
        } else {
            lazyOpenConflictsView.importAndExecute('ShowConflicts', this.props.item);
            logMeetingMessageAction(
                'ShowConflicts',
                this.props.item,
                this.props.entrySource,
                additionalProps
            );
        }
    };

    private initializeAdjacentAndConflictingMeetingsIfNeeded() {
        const { item, viewState } = this.props;

        // Only issue GetItem when there hasn't been a call out yet.
        // Note that viewState is null everytime right after user clicks on this calendar item and before the meetingRequestViewState hasn't been initialized yet
        if (viewState !== null && viewState.conflictsRequestStatus === RequestStatus.NotInitiated) {
            initializeAdjacentAndConflictingMeetingsAction(item, viewState);
        }
    }

    private getClickHandler = (hasConflicts: boolean) => {
        if (isTimePanelAvailable()) {
            return this.onShowHideConflictsInSidePanel;
        }

        // If the feature is disabled, only make this a button if we have a list to show
        if (hasConflicts) {
            return this.onShowHideConflicts;
        }

        return undefined;
    };
}
